// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODES, ERROR_MESSAGES } from '@/api/constants';
import {
  getHumanFormattedError,
  transformAndDispatchError,
} from '@/api/error-handler';
import {
  ConflictingAsset,
  ErrorCode,
  ProviderError,
  WellFormedError,
} from '@/api/types';
import { pluralize } from '@/utilities/pluralize';
import { AxiosError } from 'axios';

/**
 * Type guard to check if a string is a valid ErrorCode
 */
function isErrorCode(type: string): type is ErrorCode {
  return type in ERROR_MESSAGES;
}

/**
 * Checks if an error is an API response error containing at least one unique
 * violation error.
 */
export function hasUniqueViolationError(error: AxiosError) {
  const firstError = transformAndDispatchError(error);

  return firstError.errorType === 'UNIQUE_VIOLATION';
}

/**
 * Gets the column ID that caused a unique violation error.
 * Returns undefined if the error is not a unique violation or doesn't have column info.
 */
export function getUniqueViolationColumnId(
  error: AxiosError
): string | undefined {
  const apiError = handleAPIError(error);
  if (apiError.errorType === 'UNIQUE_VIOLATION') {
    return apiError.entityColumnId;
  }
  return undefined;
}

/**
 * Checks if an error is an API response error containing at least one
 * intersection violation error.
 */
export function hasIntersectionViolationError(error: AxiosError) {
  const firstError = transformAndDispatchError(error);

  return firstError.errorType === 'INTERSECTION_VIOLATION';
}

/**
 * Checks if an error is an API response error containing at least one
 * range violation error.
 */
export function hasRangeViolationError(error: AxiosError) {
  const firstError = transformAndDispatchError(error);

  return firstError.errorType === 'RANGE_VIOLATION';
}

/**
 * Checks if an error indicates a duplicate SKU already exists.
 */
export function hasSkuExistsError(error: AxiosError) {
  const firstError = transformAndDispatchError(error);

  return firstError.errorType === 'sku_exists';
}

/**
 * Checks if an error indicates a project was sent ending before it starts.
 */
export function hasProjectEndsBeforeItStartsError(error: AxiosError) {
  const firstError = transformAndDispatchError(error);

  return firstError.errorType === 'project_ends_before_it_starts';
}

/**
 * Checks if an error indicates a project was sent without a name.
 */
export function hasProjectNameRequiredError(error: AxiosError) {
  const firstError = transformAndDispatchError(error);

  return firstError.errorType === 'project_name_required';
}

/**
 * Checks if an error indicates a duplicate location name already exists.
 */
export function hasLocationDuplicateNameError(error: AxiosError) {
  const firstError = transformAndDispatchError(error);

  return firstError.errorType === 'location_duplicate_name';
}

/**
 * Checks if an error indicates a zone name matches its parent site name.
 */
export function hasZoneNameMatchesParentError(error: AxiosError) {
  const firstError = transformAndDispatchError(error);

  return firstError.errorType === 'zone_name_matches_parent';
}

/**
 * Handles and processes API errors, formatting them into a well-formed error object
 * for consistent error handling across the application.
 *
 * @param {AxiosError} error The Axios error from an API call or React Query mutation.
 * @return {WellFormedError} A well-formed error object with parsed error details.
 */
export function handleAPIError(error: AxiosError): WellFormedError {
  return transformAndDispatchError(error);
}

export const SUPPORT_FALLBACK_ERROR_MESSAGE = 'An error occurred.';

/**
 * Builds the message shown when a move contradicts one already recorded for its asset.
 * Names the first conflicting serial, and says how many other assets are affected when
 * the operation covered more than one.
 */
export function getMoveConflictMessage(assets?: ConflictingAsset[]): string {
  const first = assets === undefined ? undefined : assets[0];
  if (assets === undefined || first === undefined) {
    return 'This conflicts with a movement already recorded for the asset. Correct the dates on the conflicting move before continuing';
  }

  const others = assets.length - 1;
  if (others === 0) {
    return `Serial ${first.serial} has a conflicting movement. Correct its dates before continuing`;
  }

  return `Serial ${first.serial} has a conflicting movement, along with ${pluralize(others, { singular: 'other asset' })}. Correct their dates before continuing`;
}

/** Heading and body for the alert shown when a 3PL rejects a request. */
interface ProviderRejectionCopy {
  title: string;
  body: string;
}

/**
 * Renders one provider error. Logistics Plus marks title and detail nullable and routinely
 * sends only one of them, so each stands alone when the other is missing and the code is the
 * last resort. Mirrors rejectionReason in the backend's logisticsplus/provider_error.go.
 */
function providerErrorLine(error: ProviderError): string {
  if (error.title !== '' && error.detail !== '') {
    return `${error.title}\n${error.detail}`;
  }
  if (error.title !== '') {
    return error.title;
  }
  if (error.detail !== '') {
    return error.detail;
  }
  return error.code ?? '';
}

/**
 * Builds the alert copy shown when a 3PL rejects a request, relaying the provider's own
 * words for every error it reported. Returns undefined when the provider said nothing, so
 * the caller falls back to curated copy.
 *
 * The closing line is truthful because the backend always alerts on a rejection.
 */
export function getProviderRejection(
  errors?: ProviderError[]
): ProviderRejectionCopy | undefined {
  if (errors === undefined || errors.length === 0) {
    return undefined;
  }

  // An error the provider left entirely empty is dropped rather than rendered as a blank
  // line, matching the backend, which skips it in the log and the order's 3PL trail
  const lines = errors.map(providerErrorLine).filter((line) => line.length > 0);
  if (lines.length === 0) {
    return undefined;
  }

  const title =
    lines.length === 1
      ? 'Your 3PL returned the following error'
      : 'Your 3PL returned the following errors';

  return {
    title,
    body: `${lines.join('\n\n')}\n\nHardfin support has been notified to investigate.`,
  };
}

/**
 * Returns the message a dialog shows inline, or null when the error belongs in
 * the support alert.
 */
export function toDialogErrorMessage(apiError: WellFormedError): string | null {
  const curated = getUserFacingErrorMessage(apiError);
  if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
    return curated;
  }

  // A client error the user can still act on, such as a validation refusal with
  // no curated copy of its own. The custom error code is not an HTTP status, so
  // the range reads httpStatusCode.
  if (apiError.httpStatusCode >= 400 && apiError.httpStatusCode < 500) {
    return apiError.message;
  }

  return null;
}

/**
 * Returns a user-facing message for an API error. Resolves a curated
 * string from `VIOLATION_ERROR_MESSAGES` or `ERROR_MESSAGES`, or the
 * support fallback so backend text (including the request id) never
 * reaches the user.
 */
export function getUserFacingErrorMessage(apiError: WellFormedError): string {
  const violation = getHumanFormattedError(apiError);
  if (violation !== undefined) return violation;
  if (isErrorCode(apiError.errorType)) {
    return ERROR_MESSAGES[apiError.errorType];
  }
  // A violation error (e.g. INTERSECTION_VIOLATION) sets errorType to the violation type,
  // not a curated code, so fall back to the numeric status code to resolve curated copy
  const codeType = ERROR_CODES[apiError.apiStatusCode];
  if (isErrorCode(codeType)) {
    return ERROR_MESSAGES[codeType];
  }
  return SUPPORT_FALLBACK_ERROR_MESSAGE;
}
